import { Component, Host, Prop, h } from '@stencil/core';
import '@material/web/tabs/primary-tab';
import '@material/web/tabs/secondary-tab';
import '@material/web/tabs/tabs';

/** Represents a single tab within the parent omk-tabs component.
 * Use `tabType` prop to select between primary and secondary tab styles.
 * @slot - Content for the tab label.
 */
@Component({
  tag: 'omk-tab',
  styleUrl: 'omk-tab.scss',
  shadow: true,
})
export class OmkTab {
  /** Type of the tab, either 'primary' or 'secondary'. */
  @Prop() tabType: 'primary' | 'secondary' = 'primary';

  /** Text to display inside the tab */
  @Prop() tabText: string = '';

  /** Whether the icon renders inline or stacked vertically */
  @Prop() inlineIcon: boolean = false;

  /** Whether this tab is active */
  @Prop({ reflect: true }) active: boolean = false;

  /** Whether this tab has an icon */
  @Prop() hasIcon: boolean = false;

  /** Whether the tab is icon-only */
  @Prop() iconOnly: boolean = false;

  /** Whether the tab text should be in bold or not */
  @Prop() tabEmphasis: 'normal' | 'high' = 'normal';

  /** Accessibility label for the tab */
  @Prop() ariaLabel: string | null;

  /** ID of the element this tab controls */
  @Prop() ariaControls: string | null;

  render() {
    const TabComponent = this.tabType === 'primary' ? 'md-primary-tab' : 'md-secondary-tab';

    return (
      <Host>
        <TabComponent
          active={this.active}
          aria-label={this.ariaLabel}
          aria-controls={this.ariaControls}
          inline-icon={this.inlineIcon}
        >
          {this.hasIcon && (this.iconOnly ? <slot name="icon"></slot> : <slot name="icon" slot="icon"></slot>)}

          <div class={{ 'tab-content': true, [this.tabEmphasis]: true }}>
            {this.tabText && !this.iconOnly && this.tabText}
          </div>
        </TabComponent>
      </Host>
    );
  }
}
